import type { Task } from '@cartwheel/api/v1'
import { useEffect, useRef } from 'react'
import {
  type TaskDrawerCloseReason,
  trackTaskDrawerClosed,
  trackTaskDrawerOpened,
} from './task-analytics'

interface PendingDrawerClose {
  task: Task
  canceled: boolean
}

export function useTaskDrawerAnalytics(task?: Task) {
  const openedTaskIdRef = useRef<string | null>(null)
  const closedTaskIdsRef = useRef(new Set<string>())
  const pendingCloseRef = useRef<PendingDrawerClose | null>(null)
  const taskSnapshotsRef = useRef(new Map<string, Task>())

  if (task) taskSnapshotsRef.current.set(task.id, task)

  const trackCloseOnce = (taskToClose: Task, reason: TaskDrawerCloseReason) => {
    if (closedTaskIdsRef.current.has(taskToClose.id)) return

    closedTaskIdsRef.current.add(taskToClose.id)
    trackTaskDrawerClosed(taskToClose, reason)
  }

  // A deferred close distinguishes a real unmount from React Strict Mode's effect cycle.
  // biome-ignore lint/correctness/useExhaustiveDependencies: task ID defines the drawer lifecycle
  useEffect(() => {
    if (!task) return

    const pendingClose = pendingCloseRef.current
    if (pendingClose?.task.id === task.id) {
      pendingClose.canceled = true
      pendingCloseRef.current = null
    } else if (pendingClose) {
      pendingClose.canceled = true
      trackCloseOnce(
        taskSnapshotsRef.current.get(pendingClose.task.id) ?? pendingClose.task,
        'user_closed'
      )
      pendingCloseRef.current = null
    }

    closedTaskIdsRef.current.delete(task.id)
    if (openedTaskIdRef.current !== task.id) {
      openedTaskIdRef.current = task.id
      trackTaskDrawerOpened(task)
    }

    return () => {
      const pending: PendingDrawerClose = { task, canceled: false }
      pendingCloseRef.current = pending
      queueMicrotask(() => {
        if (pending.canceled) return

        trackCloseOnce(taskSnapshotsRef.current.get(task.id) ?? task, 'user_closed')
        if (pendingCloseRef.current === pending) pendingCloseRef.current = null
      })
    }
  }, [task?.id])

  return (reason: TaskDrawerCloseReason) => {
    if (task) trackCloseOnce(task, reason)
  }
}
